import { useMemo } from 'react';

import { Box, Typography, useTheme } from '@mui/material';
import { noop } from 'lodash';
import { useTranslation } from 'next-i18next';

import PreviewVideoPlayer from '@components/share/media/previewVideoPlayer';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';
import { useBreakpointV2 } from '@helpers/use';
import { useCurriculumPreview } from '@hooks/course/courseCurriculum/useCurriculumVideoPreview';

type CurriculumVideoPreviewProps = {
  open?: boolean;
  onClose: typeof noop;
  chapterId: number;
  chapterName: string;
  courseIdName: string;
};

const CurriculumVideoPreview = ({
  open,
  onClose,
  chapterId,
  chapterName,
  courseIdName,
}: CurriculumVideoPreviewProps) => {
  const { t } = useTranslation('store_course');
  const theme = useTheme();

  const { isMobile, isTablet, isDesktop } = useBreakpointV2();
  const { videoPreviewUrl, videoPreviewSubtitles } = useCurriculumPreview(courseIdName, chapterId);

  const titleTypographyVariant = useMemo(() => {
    if (isDesktop) return 'h4';
    if (isTablet) return 'h5';
    if (isMobile) return 'h6';
  }, [isDesktop, isTablet, isMobile]);

  const subtitleTypographyVariant = useMemo(() => {
    if (isDesktop) return 'h3v2';
    if (isTablet) return 'h4';
    if (isMobile) return 'h5';
  }, [isDesktop, isTablet, isMobile]);

  return (
    <BaseModalWrapper
      open={open}
      onClose={onClose}
      maxWidth={944}
      disabledBackDrop
      keepMounted={false}
      paperStyle={{
        width: { lg: 944, md: 728, xs: 327 },
        top: { lg: '65px', md: '64px', xs: '86px' },
        position: 'absolute',
      }}
    >
      <Box sx={{ p: { lg: '32px 32px 24px 32px', md: '24px', xs: '8px 8px 24px 8px' } }} gap="4px">
        <Typography variant={titleTypographyVariant} color={theme.palette.textV2.secondary}>
          {t('course_detail.course_curriculum.video_preview.title')}
        </Typography>
        <Typography variant={subtitleTypographyVariant} color={theme.palette.textV2.primary}>
          {chapterName}
        </Typography>
      </Box>
      <Box
        sx={{
          p: { lg: '0 32px 32px 32px', md: '0 24px 24px 24px', xs: '0 8px 8px 8px' },
        }}
      >
        <Box sx={{ borderRadius: '8px', position: 'relative', overflow: 'hidden' }}>
          <PreviewVideoPlayer
            videoUrl={videoPreviewUrl!}
            subtitles={videoPreviewSubtitles}
            className="curriculum-preview-video-player"
          />
        </Box>
      </Box>
    </BaseModalWrapper>
  );
};

export default CurriculumVideoPreview;
